'use client';

import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import s from './ReportsFilter.module.scss';
import Popover from '../Popover/Popover';
import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import { t } from '../../i18n';
import Icon from '../Icon';
import Checkbox from '../Checkbox/Checkbox';

const ReportsFilter = ({
    tags = [],
    authors = [],
    countries = [],
    searchUrl,
    rootUrl,
    onQueryLoadingChange,
    onActiveFiltersChange,
}) => {
    const popoverRef = useRef(null);
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedTags, setSelectedTags] = useState([]);
    const [selectedAuthors, setSelectedAuthors] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);

    const archiveUrl = `${(rootUrl || '').replace(/\/+$/, '')}/archive/`;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const query = params.get('q');
        if (query) {
            setSearchValue(query);
        }

        const urlTags = params.get('tags');
        const urlAuthors = params.get('authors');
        const urlCountries = params.get('countries');

        if (urlTags) {
            setSelectedTags(urlTags.split(','));
        }

        if (urlAuthors) {
            setSelectedAuthors(urlAuthors.split(','));
        }

        if (urlCountries) {
            setSelectedCountries(urlCountries.split(','));
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const buildParams = () => {
        const params = new URLSearchParams(window.location.search);
        params.delete('q');
        params.delete('tags');
        params.delete('authors');
        params.delete('countries');
        return params;
    };

    const appendFacets = (params) => {
        if (selectedTags.length) {
            params.append('tags', selectedTags.join(','));
        }
        if (selectedAuthors.length) {
            params.append('authors', selectedAuthors.join(','));
        }
        if (selectedCountries.length) {
            params.append('countries', selectedCountries.join(','));
        }
        return params;
    };

    const toggle = (setter) => (value) =>
        setter((current) =>
            current.includes(value)
                ? current.filter((entry) => entry !== value)
                : [...current, value]
        );

    const toggleFilterDropdown = () => {
        setIsFilterDropdownOpen(!isFilterDropdownOpen);
    };

    const handleClickOutside = (event) => {
        if (popoverRef.current && !popoverRef.current.contains(event.target)) {
            setIsFilterDropdownOpen(false);
        }
    };

    const handleSearchChange = (value) => {
        setSearchValue(value);
    };

    const handleSearchClick = () => {
        if (
            searchValue ||
            selectedTags.length ||
            selectedAuthors.length ||
            selectedCountries.length
        ) {
            onQueryLoadingChange(true);
            const params = buildParams();

            if (searchValue) {
                params.append('q', searchValue);
            }

            appendFacets(params);

            window.location.href = `${archiveUrl}?${params.toString()}`;
        }
    };

    const handleSearchClear = () => {
        if (!searchValue) return;

        setSearchValue('');

        const params = new URLSearchParams(window.location.search);
        const hadQuery = params.has('q');
        const isOnArchivePage = window.location.pathname.includes('/archive');

        if (!hadQuery || !isOnArchivePage) {
            onQueryLoadingChange?.(false);
            return;
        }

        onQueryLoadingChange(true);

        const newParams = appendFacets(buildParams());

        window.location.href = newParams.toString().length
            ? `${archiveUrl}?${newParams.toString()}`
            : archiveUrl;
    };

    const handleTagChange = toggle(setSelectedTags);
    const handleAuthorChange = toggle(setSelectedAuthors);
    const handleCountryChange = toggle(setSelectedCountries);

    const onClearAllFilters = () => {
        if (
            !selectedTags.length &&
            !selectedAuthors.length &&
            !selectedCountries.length
        ) {
            return;
        }

        onQueryLoadingChange(true);
        const params = buildParams();

        if (searchValue) {
            params.append('q', searchValue);
        }

        const url = `${archiveUrl}?${params.toString()}`;

        window.location.href = url;
    };

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                handleSearchClick();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [searchValue]);

    const activeFilters = useMemo(() => {
        return (
            selectedTags.length +
            selectedAuthors.length +
            selectedCountries.length
        );
    }, [selectedTags, selectedAuthors, selectedCountries]);

    useEffect(() => {
        const hasActiveFilters =
            searchValue.trim().length > 0 ||
            selectedTags.length > 0 ||
            selectedAuthors.length > 0 ||
            selectedCountries.length > 0;

        onActiveFiltersChange?.(hasActiveFilters);
    }, [searchValue, selectedTags, selectedAuthors, selectedCountries]);

    return (
        <div className={s.Root}>
            <div className={s.Container}>
                <FilterDropdown
                    popoverRef={popoverRef}
                    isFilterDropdownOpen={isFilterDropdownOpen}
                    toggleFilterDropdown={toggleFilterDropdown}
                    tags={tags}
                    selectedTags={selectedTags}
                    handleTagChange={handleTagChange}
                    authors={authors}
                    selectedAuthors={selectedAuthors}
                    handleAuthorChange={handleAuthorChange}
                    countries={countries}
                    selectedCountries={selectedCountries}
                    handleCountryChange={handleCountryChange}
                    onClearAllFilters={onClearAllFilters}
                    handleSearchClick={handleSearchClick}
                    activeFilters={activeFilters}
                />
                <div className={s.SearchWrapper}>
                    <div className={s.SearchField}>
                        <Icon name="Search" className={s.SearchIcon} />
                        <input
                            className={s.SearchInput}
                            type="text"
                            placeholder={t('reportsFilter.searchKeywords')}
                            value={searchValue}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                        {searchValue && (
                            <button
                                className={s.ClearButton}
                                onClick={handleSearchClear}>
                                <Icon
                                    name="CloseSmall"
                                    className={s.ClearIcon}
                                />
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleSearchClick}
                        className={s.SearchButton}>
                        {t('mediaMonitorFilter.search')}
                    </button>
                </div>
            </div>
        </div>
    );
};

const FilterDropdown = React.memo(
    ({
        popoverRef,
        isFilterDropdownOpen,
        toggleFilterDropdown,
        tags = [],
        selectedTags,
        handleTagChange,
        authors = [],
        selectedAuthors,
        handleAuthorChange,
        countries = [],
        selectedCountries,
        handleCountryChange,
        onClearAllFilters,
        handleSearchClick,
        activeFilters,
    }) => {
        const isFilterApplied = useMemo(() => {
            return (
                selectedTags.length > 0 ||
                selectedAuthors.length > 0 ||
                selectedCountries.length > 0
            );
        });
        return (
            <div className={s.FilterDropdown}>
                <Popover
                    minWidth="233px"
                    popoverRef={popoverRef}
                    modifiers={['Filter']}
                    isPopoverOpen={isFilterDropdownOpen}
                    onClick={toggleFilterDropdown}
                    content={
                        <div className={s.PopoverContent}>
                            <Dropdown
                                modifiers={['BorderBottom']}
                                title={t('reportsFilter.country')}
                                showCheckmark={selectedCountries.length > 0}>
                                <ul className={s.CheckboxList}>
                                    {countries.map((country) => (
                                        <li
                                            key={country.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {country.label
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    country.label.slice(1)}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={country.label}
                                                checked={selectedCountries.includes(
                                                    country.label
                                                )}
                                                onChange={() =>
                                                    handleCountryChange(
                                                        country.label
                                                    )
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                            <Dropdown
                                modifiers={['BorderBottom']}
                                title={t('reportsFilter.tags')}
                                showCheckmark={selectedTags.length > 0}>
                                <ul className={s.CheckboxList}>
                                    {tags.map((tag) => (
                                        <li
                                            key={tag.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {tag.label
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    tag.label.slice(1)}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={tag.label}
                                                checked={selectedTags.includes(
                                                    tag.label
                                                )}
                                                onChange={() =>
                                                    handleTagChange(tag.label)
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                            <Dropdown
                                modifiers={['BorderBottom']}
                                title={t('reportsFilter.authors')}
                                showCheckmark={selectedAuthors.length > 0}>
                                <ul className={s.CheckboxList}>
                                    {authors.map((author) => (
                                        <li
                                            key={author.id}
                                            className={s.CheckboxItem}>
                                            <span className={s.CheckboxLabel}>
                                                {author.label}
                                            </span>
                                            <Checkbox
                                                modifier="Alternative"
                                                value={author.label}
                                                checked={selectedAuthors.includes(
                                                    author.label
                                                )}
                                                onChange={() =>
                                                    handleAuthorChange(
                                                        author.label
                                                    )
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Dropdown>
                            {isFilterApplied && (
                                <div className={s.FilterButtons}>
                                    <button
                                        className={s.ClearAllFilters}
                                        onClick={onClearAllFilters}>
                                        {t('mapFilter.clear')}
                                        <Icon
                                            name="Close"
                                            className={s.FilterIcon}
                                        />
                                    </button>
                                    <div className={s.FilterSeparator} />
                                    <button
                                        className={s.ClearAllFilters}
                                        onClick={handleSearchClick}>
                                        {t('mapFilter.done')}
                                    </button>
                                </div>
                            )}
                        </div>
                    }
                    overrideButton={
                        <Button
                            title={
                                isFilterDropdownOpen
                                    ? t('mapFilter.close')
                                    : t('mapFilter.filter')
                            }
                            modifier="Filter"
                            className={s.FilterButton}
                            size="Small"
                            icon="FilterWhite"
                            isActive={isFilterDropdownOpen}
                            isFullWidth
                            description={
                                activeFilters > 0 && `(${activeFilters})`
                            }
                        />
                    }></Popover>
            </div>
        );
    }
);

ReportsFilter.propTypes = {
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func.isRequired,
    onSearchClick: PropTypes.func.isRequired,
};

export default ReportsFilter;
